export interface Nutrition {
  macros: {
    carbs: {
      total: number;
      fiber: number;
      sugars: number;
      starch: number;
    };
    fat: {
      total: number;
      saturated: number;
      monounsaturated: number;
      polyunsaturated: number;
      trans: number;
    };
    protein: number;
  };
  micro: {
    vitamins: {
      B1: number;
      B2: number;
      B3: number;
      B6: number;
      B12: number;
      A: number;
      C: number;
      D: number;
      E: number;
    };
    minerals: {
      calcium: number;
      iron: number;
      magnesium: number;
      potassium: number;
      zinc: number;
      selenium: number;
      iodine: number;
    };
    sodium: number;
    cholesterol: number;
  };
  energy: number;
}

export interface RecipeIngredientInformation {
  amount: number;
  units: string;
  name: string;
  matchedIngredient?: string;
  /**
   * Verbatim line as the user entered it, with the literal `{amount}`
   * placeholder where the original numeric amount was (if any). Used to
   * preserve the user's exact wording (e.g. "{amount} slices of american
   * cheese") while still allowing the amount to be scaled with servings.
   */
  displayText?: string;
}

export interface RecipeIngredientSection {
  title: string;
  ingredients: RecipeIngredientInformation[];
}

export function isRecipeIngredientInformationArray(
  ingredients: Recipe['ingredients'],
): ingredients is RecipeIngredientInformation[] {
  return ingredients.length > 0 && 'name' in ingredients[0];
}

interface BaseRecipe {
  name: string;
  directions: string[];
  nutrients: Nutrition;
  description: string;
  notes: string[];
  servings: number;
  time: {
    prep: number;
    cook: number;
    total: number;
  };
  isPublic: boolean;
  restricted: boolean;
  authorizedUsers: string[];
  username?: string;
  sourceURL?: string;
}

export interface Recipe extends BaseRecipe {
  ingredients: RecipeIngredientInformation[] | RecipeIngredientSection[];
}

export interface OutputRecipe extends BaseRecipe {
  ingredients: string;
}

export interface UpdateRecipeOutput extends OutputRecipe {
  uuid: string;
}

export const EMPTY_NUTRITION: Nutrition = {
  macros: {
    carbs: {
      total: 0,
      fiber: 0,
      sugars: 0,
      starch: 0,
    },
    fat: {
      total: 0,
      saturated: 0,
      monounsaturated: 0,
      polyunsaturated: 0,
      trans: 0,
    },
    protein: 0,
  },
  micro: {
    vitamins: {
      B1: 0,
      B2: 0,
      B3: 0,
      B6: 0,
      B12: 0,
      A: 0,
      C: 0,
      D: 0,
      E: 0,
    },
    minerals: {
      calcium: 0,
      iron: 0,
      magnesium: 0,
      potassium: 0,
      zinc: 0,
      selenium: 0,
      iodine: 0,
    },
    sodium: 0,
    cholesterol: 0,
  },
  energy: 0,
};

export interface CreatedRecipe extends Recipe {
  uuid: string;
}

export interface RecipeData {
  recipe: CreatedRecipe;
  imageURL?: string;
}

export interface CannotAccessRecipeType {
  sortName: string;
  uuid: string;
  status: string;
}

export function isCannotAccessRecipeType(
  recipe: RecipeData | CannotAccessRecipeType,
): recipe is CannotAccessRecipeType {
  return 'status' in recipe;
}
